import { FC, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { useSearchParams } from 'next/navigation';
import {
  unsubscribeReslinkPickupStatsAlertDttMutation,
  UnsubscribeReslinkPickupStatsAlertInput,
  UnsubscribeReslinkPickupStatsAlertResponse,
} from '@marriott/mi-groups-graphql';
import { InlineMessages, InlineMessagesType } from '@marriott/mi-ui-library';
import { useLocaleStore } from '../../../../store';
import { UnsubscribeAlertEmailProps, UnsubscribeAlertEmailStatus } from './UnsubscribeAlertEmail.types';
import { StyledUnsubscribeAlertEmail } from './UnsubscribeAlertEmail.styles';

export const UnsubscribeAlertEmailConfig = {
  emptyLabel: 'UnsubscribeAlertEmail',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/reslink/unsubscribeemail`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UnsubscribeAlertEmail: FC<{ model: UnsubscribeAlertEmailProps }> = (props: any) => {
  const {
    unsubscribed: unsubscribedLabels,
    alreadyUnsubscribed: alreadyUnsubscribedLabels,
    apiFailureMsg,
  } = props.model;
  const { locale } = useLocaleStore();

  const [eventName, setEventName] = useState('');
  const [unsubscribeStatus, setUnsubscribeStatus] = useState<UnsubscribeAlertEmailStatus | ''>('');
  const [hasError, setHasError] = useState(false);

  const [unsubscribeAlerts, { loading }] = useMutation<UnsubscribeReslinkPickupStatsAlertResponse>(
    unsubscribeReslinkPickupStatsAlertDttMutation
  );

  const urlParams = useSearchParams();
  const id = urlParams.get('id') || '';
  const email = urlParams.get('email') || '';

  useEffect(() => {
    if (!id || !email) {
      setUnsubscribeStatus('');
      setEventName('');
      return;
    }

    const input: UnsubscribeReslinkPickupStatsAlertInput = { id, email };

    unsubscribeAlerts({
      variables: { input },
      context: {
        headers: {
          'accept-language': locale,
          'uxl-cache-disable': true,
        },
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onCompleted: data => {
        const unsubscribeResponse = data?.sales?.groupSales?.groupManagement?.unsubscribeReslinkPickupStatsAlert;

        if (!unsubscribeResponse || 'errors' in unsubscribeResponse) {
          setHasError(true);
          setUnsubscribeStatus('');
          setEventName('');
          return;
        }

        if (unsubscribeResponse.status === UnsubscribeAlertEmailStatus.UNSUBSCRIBED) {
          setUnsubscribeStatus(UnsubscribeAlertEmailStatus.UNSUBSCRIBED);
        } else {
          setUnsubscribeStatus(UnsubscribeAlertEmailStatus.ALREADY_UNSUBSCRIBED);
        }

        setEventName(unsubscribeResponse.eventName || '');
      },
      onError: () => {
        setHasError(true);
        setUnsubscribeStatus('');
        setEventName('');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, email]);

  let inlineMessageType = InlineMessagesType.Error;
  let inlineMessageContent = alreadyUnsubscribedLabels;
  if (unsubscribeStatus === UnsubscribeAlertEmailStatus.UNSUBSCRIBED) {
    inlineMessageType = InlineMessagesType.Success;
    inlineMessageContent = unsubscribedLabels;
  }

  const heading = inlineMessageContent.heading.replace('{eventName}', eventName);
  const description = inlineMessageContent.description.replace('{eventName}', eventName);

  return (
    <EditableComponent config={UnsubscribeAlertEmailConfig} {...props}>
      <StyledUnsubscribeAlertEmail
        data-testid="groups-UnsubscribeAlertEmail"
        data-component-name="o-groups-unsubscribe-alert-email"
      >
        <div className="container py-4">
          {loading ? <div className="m-spinner-m d-block mx-auto my-4"></div> : null}
          {unsubscribeStatus || hasError ? (
            <InlineMessages
              title={unsubscribeStatus ? inlineMessageContent.title : apiFailureMsg}
              type={inlineMessageType}
              {...(unsubscribeStatus && {
                messageList: [heading, description],
              })}
            />
          ) : null}
        </div>
      </StyledUnsubscribeAlertEmail>
    </EditableComponent>
  );
};
